import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Avatar, IconButton, Tooltip, CircularProgress } from "@material-ui/core";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import CloseIcon from "@material-ui/icons/Close";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SearchIcon from "@material-ui/icons/Search";
import CreateIcon from "@material-ui/icons/Create";
import OpenInFullIcon from "@material-ui/icons/Fullscreen";
import MinimizeIcon from "@material-ui/icons/Remove";
import SendIcon from "@material-ui/icons/Send";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import InsertEmoticonIcon from "@material-ui/icons/InsertEmoticon";
import GroupIcon from "@material-ui/icons/Group";
import MicIcon from "@material-ui/icons/Mic";
import StopIcon from "@material-ui/icons/Stop";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getBackendUrl } from "../../config";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import "./messenger.css";

const backendUrl = getBackendUrl();
const FLOATING_MARGIN = 8;
const keepInsideViewport = (left, top, width, height) => {
  const maxLeft = Math.max(0, window.innerWidth - width);
  const maxTop = Math.max(0, window.innerHeight - height);
  const marginX = Math.min(FLOATING_MARGIN, maxLeft / 2);
  const marginY = Math.min(FLOATING_MARGIN, maxTop / 2);
  return {
    x: Math.round(Math.min(Math.max(left, marginX), maxLeft - marginX)),
    y: Math.round(Math.min(Math.max(top, marginY), maxTop - marginY))
  };
};
const validColor = color => /^#[0-9a-f]{6}$/i.test(color || "") ? color : "#1b65ec";
const formatClock = iso => iso ? new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "";
const formatListTime = iso => {
  if (!iso) return "";
  const time = new Date(iso);
  if (time.toDateString() === new Date().toDateString()) return formatClock(iso);
  return time.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};

function UserAvatar({ person, companyId, size = 45, online = false }) {
  const border = validColor(person?.color);
  const name = person?.name || "Usuário";
  const src = person?.profileImage && companyId
    ? `${backendUrl}/public/company${companyId}/user/${encodeURIComponent(person.profileImage)}` : undefined;
  return (
    <span className="fm-avatar-wrap" style={{ width: size, height: size, borderColor: border, flex: `0 0 ${size}px` }}>
      <Avatar src={src} alt={name} className="fm-avatar" style={{ width: size - 6, height: size - 6 }}>
        {name.charAt(0).toUpperCase()}
      </Avatar>
      {online && <span className="fm-online-dot" title="Online" />}
    </span>
  );
}

function nameAndPerson(chat, currentId) {
  if (!chat) return { name: "Conversa", person: null, direct: false };
  const others = (chat.users || []).map(m => m.user).filter(u => u && +u.id !== +currentId);
  if (chat.title) return { name: chat.title, person: null, direct: false };
  return { name: others.map(u => u.name).join(", ") || "Conversa", person: others[0], direct: true };
}

function AudioClip({ src }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const format = time => {
    if (!Number.isFinite(time) || time < 0) return "0:00";
    const seconds = Math.floor(time);
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  };
  useEffect(() => {
    setPlaying(false); setCurrent(0); setDuration(0); setSpeed(1);
    return () => { if (ref.current) ref.current.pause(); };
  }, [src]);
  const toggle = async () => {
    const el = ref.current;
    if (!el) return;
    try {
      if (el.paused) await el.play();
      else el.pause();
    } catch (err) { setPlaying(false); }
  };
  const seek = event => {
    const el = ref.current;
    if (!el || !Number.isFinite(el.duration) || !el.duration) return;
    el.currentTime = Number(event.target.value);
    setCurrent(el.currentTime);
  };
  const speedUp = () => {
    const next = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1;
    setSpeed(next);
    if (ref.current) ref.current.playbackRate = next;
  };
  return (
    <div className="fm-custom-audio">
      <audio ref={ref} src={src} preload="metadata" onLoadedMetadata={event => setDuration(event.currentTarget.duration || 0)}
        onDurationChange={event => setDuration(event.currentTarget.duration || 0)}
        onTimeUpdate={event => setCurrent(event.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
      <button className="fm-audio-toggle" type="button" onClick={toggle} aria-label={playing ? "Pausar áudio" : "Reproduzir áudio"}>
        {playing ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
      </button>
      <div className="fm-audio-track">
        <input type="range" aria-label="Posição do áudio" min="0" max={duration || 0} step="0.1" value={Math.min(current, duration || 0)} onChange={seek} disabled={!duration} />
        <span>{format(current)} / {format(duration)}</span>
      </div>
      <button className="fm-audio-speed" type="button" onClick={speedUp} title="Alterar velocidade de reprodução" aria-label={`Velocidade ${speed} vezes`}>{String(speed).replace(".", ",")}×</button>
    </div>
  );
}

function AttachedMedia({ chatId, message }) {
  const [blobURL, setBlobURL] = useState(null);
  const image = /\.(png|jpe?g|gif|webp)$/i.test(message.mediaPath || "");
  const audio = /^audio\//.test(message.mediaMime || "") || (!message.mediaMime &&
    (/\.(ogg|m4a|mp3)$/i.test(message.mediaPath || "") ||
      (/\.webm$/i.test(message.mediaPath || "") && /^audio-/i.test(message.mediaName || ""))));
  const video = /^video\//.test(message.mediaMime || "") || (!audio && /\.(mp4|webm)$/i.test(message.mediaPath || ""));
  useEffect(() => {
    let alive = true;
    let objectUrl;
    api.get(`/chats/${chatId}/media/${message.id}`, { responseType: "blob" })
      .then(({ data }) => {
        objectUrl = URL.createObjectURL(data);
        if (alive) setBlobURL(objectUrl);
        else URL.revokeObjectURL(objectUrl);
      })
      .catch(() => undefined);
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [chatId, message.id]);
  if (!blobURL) return <span className="fm-attachment-placeholder">Carregando anexo...</span>;
  if (audio) return <AudioClip src={blobURL} />;
  if (video) return <video className="fm-message-video" controls preload="metadata" src={blobURL} aria-label="Vídeo enviado" />;
  return image ? (
    <a href={blobURL} target="_blank" rel="noopener noreferrer" aria-label="Abrir imagem enviada">
      <img className="fm-message-image" src={blobURL} alt={message.mediaName || "Imagem"} />
    </a>
  ) : (
    <a className="fm-attachment" href={blobURL} download={message.mediaName || "anexo.pdf"}>📄 {message.mediaName || "Baixar PDF"}</a>
  );
}

export default function FloatingMessenger() {
  const { user, isAuth, socket } = useContext(AuthContext);
  const userId = +user?.id;
  const companyId = +user?.companyId;
  const [enabledByPlan, setEnabledByPlan] = useState(false);
  const [access, setAccess] = useState({ viewAll: false, canDelete: false });
  const [scope, setScope] = useState("mine");
  const [opened, setOpened] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState("list");
  const [chats, setChats] = useState([]);
  const [chatPage, setChatPage] = useState(1);
  const [chatHasMore, setChatHasMore] = useState(false);
  const [people, setPeople] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [more, setMore] = useState(false);
  const [groupMode, setGroupMode] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const audioPartsRef = useRef([]);
  const [loading, setLoading] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const activeRef = useRef(null);
  const rootRef = useRef(null);
  const launcherRef = useRef(null);
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(0);
  const [floatingPosition, setFloatingPosition] = useState(null);
  const currentChat = chats.find(c => c.id === chatId);
  const joined = !!(currentChat?.users || []).find(member => +member.userId === userId);

  // Respeita a mesma opção de plano já usada no menu Chat Interno.
  useEffect(() => {
    if (!companyId || !isAuth) { setEnabledByPlan(false); return; }
    let valid = true;
    setEnabledByPlan(false);
    api.get(`/companies/listPlan/${companyId}`)
      .then(({ data }) => { if (valid) setEnabledByPlan(!!data?.plan?.useInternalChat); })
      .catch(() => { if (valid) setEnabledByPlan(false); });
    return () => { valid = false; };
  }, [companyId, isAuth]);
  const active = nameAndPerson(currentChat, userId);
  useEffect(() => {
    if (!isAuth || !userId || !enabledByPlan) return;
    let mounted = true;
    api.get("/chats/access").then(({ data }) => {
      if (mounted) setAccess({ viewAll: !!data.viewAll, canDelete: !!data.canDelete });
    }).catch(() => { if (mounted) setAccess({ viewAll: false, canDelete: false }); });
    return () => { mounted = false; };
  }, [userId, isAuth, enabledByPlan, opened]);

  const refreshChats = useCallback(async () => {
    if (!userId || !companyId || !enabledByPlan) return;
    try {
      const { data } = await api.get("/chats", { params: { pageNumber: 1, scope: scope === "all" && access.viewAll ? "all" : "mine" } });
      const latest = Array.isArray(data.records) ? data.records : [];
      setChats(previous => {
        if (chatPage === 1) return latest;
        const ids = new Set(latest.map(item => item.id));
        return [...latest, ...previous.filter(item => !ids.has(item.id))];
      });
      if (chatPage === 1) setChatHasMore(!!data.hasMore);
    } catch (err) { /* pode acontecer durante uma troca de sessão */ }
  }, [userId, companyId, enabledByPlan, scope, access.viewAll, chatPage]);

  const loadMoreChats = async () => {
    if (!chatHasMore || loading) return;
    setLoading(true);
    try {
      const { data } = await api.get("/chats", { params: { pageNumber: chatPage + 1, scope: scope === "all" && access.viewAll ? "all" : "mine" } });
      const records = Array.isArray(data.records) ? data.records : [];
      setChats(previous => {
        const ids = new Set(previous.map(item => item.id));
        return [...previous, ...records.filter(item => !ids.has(item.id))];
      });
      setChatPage(previous => previous + 1);
      setChatHasMore(!!data.hasMore);
    } catch (err) { toastError(err); } finally { setLoading(false); }
  };

  const refreshMessages = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/chats/${id}/messages`, { params: { pageNumber: 1 } });
      if (activeRef.current === id) {
        setMessages(prev => {
          if (!prev.length) return data.records || [];
          const incoming = data.records || [];
          const old = prev.filter(item => !incoming.some(other => other.id === item.id));
          return [...old, ...incoming].sort((a, b) => a.id - b.id);
        });
        setMore(!!data.hasMore);
      }
    } catch (err) {
      if (activeRef.current === id) { setChatId(null); setView("list"); }
    }
  }, []);

  useEffect(() => { activeRef.current = chatId; }, [chatId]);
  useEffect(() => {
    if (!isAuth || !userId || !companyId || !enabledByPlan) return;
    refreshChats();
    const timer = setInterval(refreshChats, opened ? 5000 : 14000);
    return () => clearInterval(timer);
  }, [isAuth, userId, companyId, opened, enabledByPlan, refreshChats]);

  useEffect(() => {
    if (!opened || !chatId) return;
    refreshMessages(chatId);
    const timer = setInterval(() => refreshMessages(chatId), 5000);
    return () => clearInterval(timer);
  }, [opened, chatId, refreshMessages]);

  useEffect(() => {
    if (!socket?.on || !userId || !companyId) return;
    const handleChange = data => {
      if (data?.action === "refresh") {
        refreshChats();
        if (opened && activeRef.current) refreshMessages(activeRef.current);
      }
    };
    socket.on(`company-${companyId}-chat`, handleChange);
    return () => socket.off(`company-${companyId}-chat`, handleChange);
  }, [socket, userId, companyId, opened, refreshChats, refreshMessages]);

  useEffect(() => {
    if (!opened || view !== "compose") return;
    let activeRequest = true;
    const timer = setTimeout(() => {
      api.get("/chats/users", { params: { search } }).then(({ data }) => {
        if (activeRequest) setPeople(data.filter(item => +item.id !== userId));
      }).catch(toastError);
    }, 250);
    return () => { activeRequest = false; clearTimeout(timer); };
  }, [opened, view, userId, search]);

  useEffect(() => {
    if (view === "chat" && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, view]);

  const unread = chats.reduce((total, chat) => {
    const ownMember = (chat.users || []).find(member => +member.userId === userId);
    return total + Math.max(0, ownMember?.unreads || 0);
  }, 0);

  const selectChat = async (chat) => {
    setChatId(chat.id);
    activeRef.current = chat.id;
    setView("chat"); setMessages([]); setPage(1); setDraft(""); setEmojiOpen(false);
    await refreshMessages(chat.id);
    const own = (chat.users || []).find(m => +m.userId === userId);
    if (own?.unreads > 0) {
      try { await api.post(`/chats/${chat.id}/read`); refreshChats(); } catch (err) { toastError(err); }
    }
  };

  const openDirect = async (id) => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/chats/direct/${id}`);
      await refreshChats();
      await selectChat(data);
      setSearch("");
    } catch (err) { toastError(err); } finally { setLoading(false); }
  };

  const createGroup = async () => {
    if (!groupName.trim() || !selectedUsers.length || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/chats", {
        title: groupName.trim(), users: selectedUsers.map(id => ({ id }))
      });
      await refreshChats();
      await selectChat(data);
      setSearch(""); setSelectedUsers([]); setGroupName(""); setGroupMode(false);
    } catch (err) { toastError(err); } finally { setLoading(false); }
  };

  const removeFromMyList = async () => {
    if (!chatId || !joined || !access.canDelete) return;
    if (!window.confirm("Remover esta conversa somente da sua lista? As mensagens continuarão disponíveis para os outros participantes.")) return;
    setLoading(true);
    try {
      await api.delete(`/chats/${chatId}`);
      setChats(previous => previous.filter(item => item.id !== chatId));
      setView("list"); setChatId(null); activeRef.current = null; setMessages([]);
      await refreshChats();
    } catch (err) { toastError(err); } finally { setLoading(false); }
  };

  const sendMessage = async (event) => {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }
    const msg = draft.trim();
    if (!chatId || !msg || sending) return;
    setSending(true);
    try {
      await api.post(`/chats/${chatId}/messages`, { message: msg });
      setDraft(""); setEmojiOpen(false);
      await Promise.all([refreshChats(), refreshMessages(chatId)]);
    } catch (err) { toastError(err); } finally { setSending(false); }
  };

  const uploadFile = async (eventOrFile) => {
    const file = eventOrFile?.target ? eventOrFile.target.files?.[0] : eventOrFile;
    if (eventOrFile?.target) eventOrFile.target.value = "";
    if (!file || !chatId) return;
    if (file.size > 20 * 1024 * 1024) return window.alert("O arquivo deve ter no máximo 20 MB.");
    const body = new FormData(); body.append("file", file);
    setSending(true);
    try {
      await api.post(`/chats/${chatId}/media`, body, { headers: { "Content-Type": "multipart/form-data" } });
      await Promise.all([refreshChats(), refreshMessages(chatId)]);
    } catch (err) { toastError(err); } finally { setSending(false); }
  };

  const toggleAudio = async () => {
    if (recording) {
      if (recorderRef.current) {
        recorderRef.current.stop();
      }
      setRecording(false);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      window.alert("Este navegador não suporta gravação de áudio."); return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const allowed = ["audio/webm", "audio/ogg", "audio/mp4"].find(type => MediaRecorder.isTypeSupported(type));
      if (!allowed) throw new Error("Nenhum formato de gravação compatível.");
      const recorder = new MediaRecorder(stream, { mimeType: allowed });
      audioPartsRef.current = [];
      recorder.ondataavailable = event => { if (event.data.size) audioPartsRef.current.push(event.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audio = new Blob(audioPartsRef.current, { type: allowed });
        if (audio.size > 0) {
          const extension = allowed === "audio/ogg" ? "ogg" : allowed === "audio/mp4" ? "m4a" : "webm";
          await uploadFile(new File([audio], `audio-${Date.now()}.${extension}`, { type: allowed }));
        }
      };
      recorderRef.current = recorder;
      recorder.start(); setRecording(true);
    } catch (err) {
      if (stream) stream.getTracks().forEach(track => track.stop());
      setRecording(false);
      window.alert("Não foi possível acessar o microfone. Confira a permissão no navegador.");
    }
  };

  const loadPrevious = async () => {
    if (!chatId || !more || loading) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/chats/${chatId}/messages`, { params: { pageNumber: page + 1 } });
      if (activeRef.current === chatId) {
        setMessages(prev => [...data.records, ...prev.filter(item => !data.records.some(old => old.id === item.id))]);
        setPage(prev => prev + 1); setMore(!!data.hasMore);
      }
    } catch (err) { toastError(err); } finally { setLoading(false); }
  };

  const filtered = chats.filter(chat => nameAndPerson(chat, userId).name.toLowerCase().includes(search.toLowerCase()));
  const foundPeople = people.filter(person => person.name.toLowerCase().includes(search.toLowerCase()));

  // A posição fica somente no navegador deste usuário e desta empresa.
  useLayoutEffect(() => {
    if (!isAuth || !enabledByPlan || !userId || !companyId || !launcherRef.current) return;
    const bounds = launcherRef.current.getBoundingClientRect();
    let saved;
    try {
      saved = JSON.parse(window.localStorage.getItem(`agilizachat:mensagens:posicao:${companyId}:${userId}`));
    } catch (_) { /* Navegação privada pode indisponibilizar o armazenamento. */ }
    const safeSaved = saved && Number.isFinite(saved.x) && Number.isFinite(saved.y);
    const initialLeft = window.innerWidth - bounds.width - (window.innerWidth <= 480 ? 8 : 20);
    const initialTop = window.innerHeight - bounds.height - (window.innerWidth <= 480 ? 8 : 18);
    setFloatingPosition(keepInsideViewport(
      safeSaved ? saved.x : initialLeft,
      safeSaved ? saved.y : initialTop,
      bounds.width,
      bounds.height
    ));
  }, [isAuth, enabledByPlan, companyId, userId]);

  // A janela segue o botão, escolhendo o lado com mais espaço disponível.
  // O painel nunca precisa ultrapassar as bordas para que o botão seja arrastável.
  const positionPanel = useCallback(() => {
    const panel = panelRef.current;
    const launcher = launcherRef.current;
    if (!panel || !launcher) return;
    const button = launcher.getBoundingClientRect();
    panel.style.maxHeight = "";
    const panelRect = panel.getBoundingClientRect();
    const gap = 12;
    const margin = FLOATING_MARGIN;
    const above = Math.max(0, button.top - gap - margin);
    const below = Math.max(0, window.innerHeight - button.bottom - gap - margin);
    const openAbove = above >= panelRect.height || above >= below;
    const availableHeight = openAbove ? above : below;
    const height = Math.min(panelRect.height, Math.max(0, availableHeight));
    panel.style.maxHeight = `${height}px`;
    const maxLeft = Math.max(margin, window.innerWidth - panelRect.width - margin);
    const left = Math.min(Math.max(button.right - panelRect.width, margin), maxLeft);
    const top = openAbove ? button.top - height - gap : button.bottom + gap;
    panel.style.position = "fixed";
    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = `${Math.round(Math.max(margin, top))}px`;
    panel.style.right = "auto";
    panel.style.bottom = "auto";
  }, []);

  useLayoutEffect(() => {
    if (opened) positionPanel();
  }, [opened, expanded, floatingPosition, positionPanel]);

  // Ao redimensionar a tela, mantenha o botão e a janela totalmente visíveis.
  useEffect(() => {
    if (!isAuth || !enabledByPlan || !userId || !companyId) return;
    const fitToViewport = () => {
      const launcher = launcherRef.current;
      if (!launcher) return;
      const rect = rootRef.current?.getBoundingClientRect() || launcher.getBoundingClientRect();
      const safe = keepInsideViewport(rect.left, rect.top, rect.width, rect.height);
      setFloatingPosition(previous => previous?.x === safe.x && previous?.y === safe.y ? previous : safe);
      positionPanel();
    };
    window.addEventListener("resize", fitToViewport);
    return () => window.removeEventListener("resize", fitToViewport);
  }, [isAuth, enabledByPlan, companyId, userId, positionPanel]);

  const onLauncherPointerDown = event => {
    if (event.button !== 0 || event.isPrimary === false) return;
    // O botão pode expandir para a esquerda/direita ao passar o mouse.
    // A coordenada salva pertence ao círculo fixo, evitando saltos ao arrastar.
    const root = rootRef.current;
    if (!root) return;
    const rect = root.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId, startX: event.clientX, startY: event.clientY,
      x: rect.left, y: rect.top, width: rect.width, height: rect.height,
      moved: false, last: null
    };
    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const onLauncherPointerMove = event => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !rootRef.current) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < 6) return;
    drag.moved = true;
    drag.last = keepInsideViewport(drag.x + dx, drag.y + dy, drag.width, drag.height);
    // Move diretamente o elemento durante o gesto, sem renderizar todas as mensagens.
    rootRef.current.style.left = `${drag.last.x}px`;
    rootRef.current.style.top = `${drag.last.y}px`;
    rootRef.current.style.right = "auto";
    rootRef.current.style.bottom = "auto";
    if (opened) positionPanel();
  };

  const finishLauncherDrag = event => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (event.currentTarget.releasePointerCapture && event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!drag.moved || !drag.last) return;
    suppressClickRef.current = Date.now();
    setFloatingPosition(drag.last);
    try {
      window.localStorage.setItem(
        `agilizachat:mensagens:posicao:${companyId}:${userId}`,
        JSON.stringify(drag.last)
      );
    } catch (_) { /* Continua arrastável mesmo sem armazenamento local. */ }
  };

  const onLauncherClick = event => {
    if (Date.now() - suppressClickRef.current < 350) {
      event.preventDefault();
      suppressClickRef.current = 0;
      return;
    }
    setOpened(value => !value);
  };

  if (!isAuth || !userId || !companyId || !enabledByPlan) return null;
  // A etiqueta se abre para dentro da tela, sem mudar a posição do ícone arrastável.
  const expandLeft = !floatingPosition || floatingPosition.x + 194 > window.innerWidth - FLOATING_MARGIN;
  return (
    <div ref={rootRef} className={`fm-root ${expanded ? "fm-expanded" : ""} ${expandLeft ? "fm-expand-left" : ""}`}
      style={floatingPosition ? {
        left: `${floatingPosition.x}px`, top: `${floatingPosition.y}px`,
        right: "auto", bottom: "auto"
      } : undefined}>
      {opened && (
        <section ref={panelRef} className="fm-panel" aria-label="Mensagens internas">
          <header className="fm-header">
            {view !== "list" && (
              <IconButton size="small" title="Voltar para conversas" onClick={() => { setView("list"); setSearch(""); setGroupMode(false); }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            {view === "chat" ? (
              <div className="fm-person-heading">
                {active.direct ? <UserAvatar person={active.person} companyId={companyId} size={40} online={active.person?.online} /> : <span className="fm-group-icon"><GroupIcon /></span>}
                <div className="fm-heading-name">
                  <strong title={active.name}>{active.name}</strong>
                  <small>{active.direct ? (active.person?.online ? "● Online" : "Offline") : "Grupo interno"}</small>
                </div>
              </div>
            ) : (
              <div className="fm-heading-name"><strong>{view === "compose" ? "Nova conversa" : "Mensagens"}</strong>
                {view === "list" && unread > 0 && <span className="fm-count">{unread > 99 ? "99+" : unread}</span>}
              </div>
            )}
            <div className="fm-header-buttons">
              {view === "chat" && access.canDelete && joined && (
                <Tooltip title="Remover da minha lista"><IconButton size="small" disabled={loading} onClick={removeFromMyList}><DeleteOutlineIcon /></IconButton></Tooltip>
              )}
              <Tooltip title={expanded ? "Restaurar janela" : "Expandir janela"}><IconButton size="small" onClick={() => setExpanded(v => !v)}><OpenInFullIcon /></IconButton></Tooltip>
              <Tooltip title="Minimizar"><IconButton size="small" onClick={() => setOpened(false)}><MinimizeIcon /></IconButton></Tooltip>
              <Tooltip title="Fechar"><IconButton size="small" onClick={() => { setOpened(false); setView("list"); }}><CloseIcon /></IconButton></Tooltip>
            </div>
          </header>

          {view === "list" && (
            <>
              <div className="fm-search"><SearchIcon fontSize="small" /><input aria-label="Buscar conversas" placeholder="Pesquisar conversas..." value={search} onChange={e => setSearch(e.target.value)} /></div>
              {access.viewAll && <div className="fm-scope-switch">
                <button type="button" className={scope === "mine" ? "selected" : ""} onClick={() => { setChats([]); setChatPage(1); setChatHasMore(false); setScope("mine"); setSearch(""); }}>Minhas conversas</button>
                <button type="button" className={scope === "all" ? "selected" : ""} onClick={() => { setChats([]); setChatPage(1); setChatHasMore(false); setScope("all"); setSearch(""); }}><VisibilityIcon fontSize="small" /> Visão da empresa</button>
              </div>}
              {scope === "all" && access.viewAll && <div className="fm-oversight-note">Consulta administrativa de conversas da sua empresa. Fora dos seus chats, o acesso é somente leitura.</div>}
              <div className="fm-scroll fm-conversations">
                {filtered.length === 0 && <div className="fm-empty">Nenhuma conversa encontrada. Clique em <strong>Nova conversa</strong> para começar.</div>}
                {filtered.map(chat => {
                  const details = nameAndPerson(chat, userId);
                  const own = (chat.users || []).find(m => +m.userId === userId);
                  return <button type="button" className="fm-conversation" onClick={() => selectChat(chat)} key={chat.id}>
                    {details.direct ? <UserAvatar person={details.person} companyId={companyId} size={48} online={details.person?.online} /> : <span className="fm-group-icon"><GroupIcon /></span>}
                    <span className="fm-conversation-copy"><strong>{details.name}</strong><small>{chat.lastMessage || "Inicie uma conversa"}</small>{!(chat.users || []).some(m => +m.userId === userId) && <small>Somente leitura · Visão da empresa</small>}</span>
                    <span className="fm-conversation-aside"><small>{formatListTime(chat.updatedAt)}</small>{own?.unreads > 0 && <span className="fm-count">{own.unreads}</span>}</span>
                  </button>;
                })}
                {chatHasMore && <button type="button" className="fm-more" onClick={loadMoreChats} disabled={loading}>Carregar mais conversas</button>}
              </div>
              <button className="fm-compose-fab" type="button" title="Nova conversa" onClick={() => { setView("compose"); setSearch(""); }}><CreateIcon /></button>
            </>
          )}

          {view === "compose" && (
            <>
              <div className="fm-security">🔒 Somente usuários da sua empresa. Conversas de trabalho podem ser consultadas por administradores autorizados.</div>
              <div className="fm-search"><SearchIcon fontSize="small" /><input placeholder="Buscar pessoas..." aria-label="Buscar usuários" value={search} onChange={e => setSearch(e.target.value)} /></div>
              <button className="fm-start-group" type="button" onClick={() => { setGroupMode(!groupMode); setSelectedUsers([]); }}><GroupIcon fontSize="small" /> {groupMode ? "Voltar às conversas individuais" : "Criar grupo"}</button>
              {groupMode && (
                <div className="fm-group-create"><input maxLength={150} placeholder="Nome do grupo" value={groupName} onChange={e => setGroupName(e.target.value)} />
                  <button type="button" disabled={!groupName.trim() || !selectedUsers.length || loading} onClick={createGroup}>Criar grupo ({selectedUsers.length})</button>
                </div>
              )}
              <div className="fm-scroll fm-people">
                {foundPeople.map(person => (
                  <button key={person.id} type="button" className="fm-person-row" disabled={loading} onClick={() => groupMode ? setSelectedUsers(prev => prev.includes(person.id) ? prev.filter(id => id !== person.id) : [...prev, person.id]) : openDirect(person.id)}>
                    {groupMode && <input type="checkbox" checked={selectedUsers.includes(person.id)} readOnly tabIndex={-1} />}
                    <UserAvatar person={person} companyId={companyId} size={42} online={person.online} />
                    <span><strong>{person.name}</strong><small>{person.online ? "Online" : "Offline"}</small></span>
                  </button>
                ))}
                {!foundPeople.length && <div className="fm-empty">Nenhum usuário encontrado na sua empresa.</div>}
              </div>
            </>
          )}

          {view === "chat" && chatId && (
            <>
              <div className="fm-scroll fm-message-list">
                {more && <button className="fm-more" type="button" onClick={loadPrevious} disabled={loading}>Carregar mensagens anteriores</button>}
                {messages.length === 0 && <div className="fm-empty">Envie uma mensagem para começar esta conversa.</div>}
                {messages.map(message => (
                  <div className={`fm-message-line ${+message.senderId === userId ? "fm-mine" : "fm-other"}`} key={message.id}>
                    {+message.senderId !== userId && <UserAvatar person={message.sender} companyId={companyId} size={27} />}
                    <div className="fm-bubble">
                      {currentChat?.title && +message.senderId !== userId && <strong className="fm-sender-name">{message.sender?.name || "Usuário"}</strong>}
                      {message.mediaPath ? <AttachedMedia chatId={chatId} message={message} /> : <span className="fm-message-text">{message.message}</span>}
                      <small>{formatClock(message.createdAt)}</small>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              {!joined && <div className="fm-oversight-note">Você está consultando uma conversa de outros participantes. Somente leitura.</div>}
              {joined && emojiOpen && <div className="fm-emojis">{["😊", "😂", "❤️", "👍", "👏", "🎉", "🙏", "🔥", "✅", "😃"].map(e => <button type="button" key={e} onClick={() => { setDraft(v => v + e); setEmojiOpen(false); }}>{e}</button>)}</div>}
              {joined && <form className="fm-input-bar" onSubmit={sendMessage}>
                <IconButton size="small" title="Inserir emoji" onClick={() => setEmojiOpen(v => !v)}><InsertEmoticonIcon /></IconButton>
                <input maxLength={10000} placeholder="Mensagem..." aria-label="Digite sua mensagem" value={draft} onChange={e => setDraft(e.target.value)} />
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,video/mp4,video/webm,.docx,.xlsx,.txt,.csv" hidden onChange={uploadFile} />
                <IconButton size="small" title="Enviar imagem, PDF, vídeo ou documento (até 20 MB)" disabled={sending || recording} onClick={() => fileRef.current?.click()}><AttachFileIcon /></IconButton>
                <IconButton size="small" title={recording ? "Parar e enviar áudio" : "Gravar áudio"} disabled={sending} onClick={toggleAudio} style={{ color: recording ? "#f23f55" : undefined }}>
                  {recording ? <StopIcon /> : <MicIcon />}
                </IconButton>
                <button className="fm-send" type="submit" disabled={sending || recording || !draft.trim()}>{sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}</button>
              </form>}
            </>
          )}
        </section>
      )}
      <button ref={launcherRef} type="button" className="fm-launcher"
        onPointerDown={onLauncherPointerDown}
        onPointerMove={onLauncherPointerMove}
        onPointerUp={finishLauncherDrag}
        onPointerCancel={finishLauncherDrag}
        onDragStart={event => event.preventDefault()}
        onTransitionEnd={event => { if (event.propertyName === "width" && opened) positionPanel(); }}
        onMouseEnter={() => { if (opened) positionPanel(); }}
        onMouseLeave={() => { if (opened) positionPanel(); }}
        onClick={onLauncherClick} aria-expanded={opened}
        aria-label={opened ? "Fechar mensagens internas" : "Abrir mensagens internas"}
        title="Clique para abrir ou arraste para mover as mensagens">
        <ForumOutlinedIcon className="fm-launcher-icon" />
        <strong className="fm-launcher-label">Mensagens</strong>
        {unread > 0 && <span className="fm-count">{unread > 99 ? "99+" : unread}</span>}
        <span className="fm-launcher-user"><UserAvatar person={user} companyId={companyId} size={31} online /></span>
      </button>
    </div>
  );
}
